import { ReactElement, useState } from 'react';
import toast from 'react-hot-toast';
import RadioOption from '../../../../components/RadioOption';
import OptionSection from './OptionSection';

interface DataSourceSectionProps {
  session: any;
  updateSession: any;
  invalidateSession: () => Promise<void>;
}

/**
 * Session-level data source for device runs — two settings, because two
 * different questions are being asked.
 *
 * **Upload** (`chunk_upload`) is about where a run's data comes from when it
 * first arrives. File upload (default): the device zip is authoritative and
 * the chunk pipeline only backfills episode linkage. Chunk upload: the runs
 * are created from the live blob stream instead. This applies to runs
 * registered from now on — it cannot retroactively change how an existing
 * run was uploaded.
 *
 * **Reprocess** (`reprocess_from_blob`) is about what happens every time a
 * run is reprocessed. Stored file (default): reprocess the artifact already
 * held. Rebuild from blob: re-extract the run from the episode blob chunks
 * with the current reassembly code first, so decoder improvements reach old
 * runs. This one applies to every run in the session, existing ones
 * included — the server propagates it on save.
 */
export default function DataSourceSection({
  session,
  updateSession,
  invalidateSession,
}: DataSourceSectionProps): ReactElement {
  const [isUpdating, setIsUpdating] = useState(false);
  const chunkUpload = session?.chunk_upload === true;
  const reprocessFromBlob = session?.reprocess_from_blob === true;

  const applyChange = async (patch: Record<string, boolean>, message: string) => {
    if (isUpdating) return;
    try {
      setIsUpdating(true);
      await updateSession.mutateAsync(patch);
      toast.success(message);
      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error updating data source');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUploadChange = async (next: boolean) => {
    if (next === chunkUpload) return;
    await applyChange({ chunk_upload: next }, `Upload source set to ${next ? 'chunk upload' : 'file upload'}`);
  };

  const handleReprocessChange = async (next: boolean) => {
    if (next === reprocessFromBlob) return;
    await applyChange(
      { reprocess_from_blob: next },
      `Reprocess source set to ${next ? 'blob rebuild' : 'stored file'} for this session's runs`
    );
  };


  return (
    <OptionSection
      title="Data Source"
      status={
        <span className="cr-text-sm cr-text-gray-500">
          {chunkUpload ? 'chunk upload' : 'file upload'}
          {' / '}
          {reprocessFromBlob ? 'blob on reprocess' : 'file on reprocess'}
        </span>
      }
    >
      <div className="cr-mb-4 cr-flex cr-flex-col cr-gap-5 cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md">
        <div className="cr-flex cr-flex-col cr-gap-2">
          <div className="cr-text-sm cr-font-semibold cr-text-gray-700">Upload &amp; initial processing</div>
          <RadioOption
            name="sessionUploadSource"
            value="file"
            checked={!chunkUpload}
            onChange={() => handleUploadChange(false)}
            disabled={isUpdating}
            label="File Upload"
            hint="(default — device zip is authoritative)"
          />
          <RadioOption
            name="sessionUploadSource"
            value="chunks"
            checked={chunkUpload}
            onChange={() => handleUploadChange(true)}
            disabled={isUpdating}
            label="Chunk Upload"
            hint="(runs created from the live blob stream)"
          />
          <p className="cr-text-xs cr-text-gray-500">
            Applies to runs registered from now on — how a run already in the session arrived cannot be changed.
          </p>
        </div>

        <div className="cr-flex cr-flex-col cr-gap-2">
          <div className="cr-text-sm cr-font-semibold cr-text-gray-700">Reprocessing</div>
          <RadioOption
            name="sessionReprocessSource"
            value="file"
            checked={!reprocessFromBlob}
            onChange={() => handleReprocessChange(false)}
            disabled={isUpdating}
            label="Stored File"
            hint="(default — reprocess the artifact as held)"
          />
          <RadioOption
            name="sessionReprocessSource"
            value="blob"
            checked={reprocessFromBlob}
            onChange={() => handleReprocessChange(true)}
            disabled={isUpdating}
            label="Rebuild from Blob"
            hint="(re-extract from the device blobs each reprocess)"
          />
          <p className="cr-text-xs cr-text-gray-500">
            Applies to every run in this session, existing runs included. Rebuilding picks up decoder improvements
            but makes each reprocess slower; it only affects runs that have device blobs stored.
          </p>
        </div>
      </div>
    </OptionSection>
  );
}
